import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Gift, Copy, Share2, Users, CheckCircle2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function fetchReferral() {
  const res = await fetch(`${API}/referrals/me`, { credentials: "include" });
  if (!res.ok) throw new Error("referral fetch failed");
  return res.json();
}

export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await fetchReferral();
        if (!cancelled) setData(d);
      } catch {
        /* gracefully hide */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Always rebuild the share URL from the *current* live origin so links
  // shared from cartiq360.in carry the cartiq360.in domain (#3 — feedback fix).
  // The backend's APP_URL env can lag during deploy; the browser knows the
  // truth.
  const shareUrl = useMemo(() => {
    if (!data?.code) return data?.shareUrl || "";
    if (typeof window === "undefined") return data.shareUrl || "";
    return `${window.location.origin}/?ref=${data.code}`;
  }, [data?.code, data?.shareUrl]);

  const shareText = useMemo(() => {
    if (!data?.code) return data?.shareText || "";
    return `I'm using CartIQ 360 to track every Blinkit/Zepto/Swiggy/Zomato order. Sign up via my link and we both get 1 month of Pro free → ${shareUrl}`;
  }, [data?.code, data?.shareText, shareUrl]);

  if (loading || !data) return null;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Referral link copied.");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Could not copy.");
    }
  };

  const onShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "CartIQ 360 — track your real food spend",
          text: shareText,
          url: shareUrl,
        });
      } catch {
        /* user dismissed */
      }
    } else {
      onCopy();
    }
  };

  return (
    <div className="cartiq-card relative overflow-hidden" data-testid="referral-card">
      <div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: "linear-gradient(90deg,#1D9E75 0%,#FF6B00 50%,#9D3CFF 100%)" }}
      />
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#1D9E75] font-bold mb-1">
            — Refer & earn
          </div>
          <h2 className="font-display uppercase text-2xl flex items-center gap-2">
            <Gift className="w-5 h-5 text-[#1D9E75]" /> 1 month of Pro free
          </h2>
          <p className="text-xs text-[#888] mt-1.5 leading-relaxed">
            Share your link. When a friend signs up and starts any paid plan, we add
            <span className="text-[#F0EDE8] font-bold"> 30 days of Pro </span>
            to your access — automatically.
          </p>
        </div>
      </div>

      {/* Code + share row */}
      <div className="grid sm:grid-cols-[1fr_auto] gap-2 mb-4">
        <div
          className="flex items-center gap-2 px-3 py-2.5 bg-[#14141A] border border-[#2A2A35] font-mono-num text-sm overflow-hidden"
          data-testid="referral-code-row"
        >
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#555] shrink-0">CODE</span>
          <span
            className="text-[#FF6B00] tracking-[0.22em] font-bold shrink-0"
            data-testid="referral-code"
          >
            {data.code}
          </span>
          <span className="text-[#555]">·</span>
          <span
            className="text-[#888] truncate"
            title={shareUrl}
            data-testid="referral-link"
          >
            {shareUrl.replace(/^https?:\/\//, "")}
          </span>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCopy}
            className="btn-ghost flex items-center gap-2 px-3"
            data-testid="referral-copy-btn"
          >
            {copied ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-[#1D9E75]" /> Copied
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" /> Copy
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onShare}
            className="btn-saffron flex items-center gap-2 px-3"
            data-testid="referral-share-btn"
          >
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-3 gap-3 pt-4 border-t border-[#2A2A35]">
        <div data-testid="referral-stat-friends">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#888] flex items-center gap-1.5">
            <Users className="w-3 h-3" /> Friends joined
          </div>
          <div className="font-mono-num text-2xl mt-1">{data.friendsSignedUp}</div>
        </div>
        <div data-testid="referral-stat-paid">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#888] flex items-center gap-1.5">
            <CheckCircle2 className="w-3 h-3" /> Subscribed
          </div>
          <div className="font-mono-num text-2xl mt-1 text-[#1D9E75]">{data.friendsPaid}</div>
        </div>
        <div data-testid="referral-stat-months">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#888] flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Free months
          </div>
          <div className="font-mono-num text-2xl mt-1 text-[#FF6B00]">
            {data.freeMonthsEarned}
          </div>
        </div>
      </div>

      {data.creditDays > 0 && (
        <div
          className="mt-4 px-3 py-2 bg-[#1D9E75]/10 border-l-[3px] border-[#1D9E75] text-xs text-[#F0EDE8]"
          data-testid="referral-credit-banner"
        >
          You've earned{" "}
          <span className="font-bold text-[#1D9E75]">{data.creditDays} bonus days</span>{" "}
          of Pro access. Keep sharing.
        </div>
      )}
    </div>
  );
}
